import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, AlertTriangle, Clock, Truck } from "lucide-react";
import { api } from "../lib/api";
import AddressAutocomplete from "./AddressAutocomplete";

/**
 * Live price calculator — sits at the top of the home hero.
 * 2 address fields (pickup + delivery) — DK addresses only.
 * On change (debounced 600ms) calls /api/quote and shows a live cirka-pris.
 * "Book denne levering" hands the addresses off to /book via URL params.
 *
 * Designed for fast trust-building: visitor sees a price WITHOUT entering
 * personal data, BEFORE the booking funnel.
 */

// Cheap DK address parser — splits "Storgade 5, 4000 Roskilde" into parts.
function parseAddress(raw) {
  if (!raw) return null;
  const m = String(raw).trim().match(/^(.+?),\s*(\d{4})\s+(.+)$/);
  if (!m) return null;
  return { address: m[1].trim(), zip: m[2], city: m[3].trim() };
}

export default function PriceCalculatorHero() {
  const nav = useNavigate();
  const [pickup, setPickup] = React.useState("");
  const [delivery, setDelivery] = React.useState("");
  const [state, setState] = React.useState({ status: "idle", quote: null, error: "" });
  const [pulse, setPulse] = React.useState(0);

  const pickupParts = parseAddress(pickup);
  const deliveryParts = parseAddress(delivery);
  const ready = pickupParts && deliveryParts;

  // Debounced quote fetch when both addresses are parseable
  React.useEffect(() => {
    if (!pickupParts || !deliveryParts) {
      setState((s) => (s.status === "idle" ? s : { status: "idle", quote: null, error: "" }));
      return;
    }
    setState((s) => (s.status === "loading" ? s : { ...s, status: "loading", error: "" }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", {
          pickup_address: pickupParts.address,
          pickup_zip: pickupParts.zip,
          pickup_city: pickupParts.city,
          delivery_address: deliveryParts.address,
          delivery_zip: deliveryParts.zip,
          delivery_city: deliveryParts.city,
          delivery_type: "same_day",
        });
        setState({ status: "ready", quote: data, error: "" });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Kunne ikke beregne pris — tjek adresserne";
        setState({ status: "error", quote: null, error: msg });
      }
    }, 700);
    return () => clearTimeout(timer);
    // We intentionally only watch the raw string inputs so the effect is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, delivery]);

  // Tiny pulse animation when price changes — guard against unmount
  const lastTotalRef = React.useRef(null);
  React.useEffect(() => {
    const t = state.quote?.total_dkk ?? null;
    if (t !== null && t !== lastTotalRef.current) {
      lastTotalRef.current = t;
      setPulse((p) => p + 1);
    }
  }, [state.quote]);

  const goToBook = () => {
    if (!ready) return;
    const qs = new URLSearchParams({
      pa: pickupParts.address,
      pz: pickupParts.zip,
      pc: pickupParts.city,
      da: deliveryParts.address,
      dz: deliveryParts.zip,
      dc: deliveryParts.city,
      type: "same_day",
    });
    nav(`/book?${qs.toString()}`);
  };

  return (
    <div className="bg-white border border-brand-dark shadow-[8px_8px_0_rgba(220,38,38,0.95)] p-6 sm:p-8" data-testid="price-calculator-hero">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-brand-red"></div>
        <div className="kicker">Beregn din pris på 5 sekunder</div>
      </div>
      <h3 className="font-display font-semibold text-2xl text-brand-dark mt-3 tracking-tight">Hvad koster det at sende fra A til B?</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div>
          <label className="label-flat flex items-center gap-1.5"><MapPin size={11} className="text-brand-red" /> Fra (afhentning)</label>
          <AddressAutocomplete
            value={pickup}
            onChange={setPickup}
            placeholder="fx Storgade 5, 1100 København K"
            testId="calc-pickup"
          />
        </div>
        <div>
          <label className="label-flat flex items-center gap-1.5"><MapPin size={11} className="text-brand-red" /> Til (levering)</label>
          <AddressAutocomplete
            value={delivery}
            onChange={setDelivery}
            placeholder="fx Hovedgaden 12, 4000 Roskilde"
            testId="calc-delivery"
          />
        </div>
      </div>

      {/* Hint when addresses look incomplete */}
      {(pickup || delivery) && !ready && (
        <div className="mt-3 text-xs text-brand-text2" data-testid="calc-hint">
          Skriv komplet adresse, fx: <span className="font-mono text-brand-dark">Storgade 5, 4000 Roskilde</span>
        </div>
      )}

      {/* Result panel */}
      {ready && (
        <div className="mt-5 border-t border-brand pt-5" data-testid="calc-result">
          {state.status === "loading" && (
            <div className="flex items-center gap-2 text-brand-text2 text-sm" data-testid="calc-loading">
              <Loader2 className="animate-spin text-brand-red" size={14} /> Beregner pris og rute…
            </div>
          )}
          {state.status === "error" && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 text-sm flex items-start gap-2" data-testid="calc-error">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <div>{state.error}</div>
            </div>
          )}
          {state.status === "ready" && state.quote && (
            <div key={pulse} className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:items-end">
              <div className="sm:col-span-2 space-y-1">
                <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-brand-text2">
                  <Truck size={12} className="text-brand-red" /> Same-Day Kurér · cirka-pris
                </div>
                <div className="text-xs text-brand-text2">
                  {Number(state.quote.distance_km).toLocaleString("da-DK", { maximumFractionDigits: 1 })} km rute · {state.quote.estimated_duration_min || "?"} min køretid
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-text2 mt-1.5">
                  <span>Startpris <strong className="text-brand-dark">199 kr</strong> inkl. 8 km</span>
                  {state.quote.distance_fee_dkk > 0 && <span>+ {Number(state.quote.extra_km).toLocaleString("da-DK", { maximumFractionDigits: 1 })} km × {state.quote.price_per_extra_km || 6} kr = <strong className="text-brand-dark">{Number(state.quote.distance_fee_dkk).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</strong></span>}
                </div>
              </div>
              <div className="text-right sm:text-right">
                <div className="text-[10px] tracking-widest uppercase text-brand-text2">Cirka-total</div>
                <div className="font-display font-bold text-3xl text-brand-dark mt-0.5 tabular-nums">{Number(state.quote.total_dkk).toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <span className="text-base text-brand-text2 font-medium">kr</span></div>
                <div className="text-[10px] text-brand-text2 mt-0.5">Inkl. 25% moms</div>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        onClick={goToBook}
        disabled={!ready}
        className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="calc-book-button"
      >
        Book denne levering <ArrowRight size={16} />
      </button>

      <div className="flex items-center gap-1.5 text-[11px] text-brand-text2 mt-3" data-testid="calc-footnote">
        <Clock size={11} className="text-brand-red" /> Cirka-pris baseret på Same-Day. Tillæg for weekend (+20%) og sen aften (+15%) tilføjes i booking.
      </div>
    </div>
  );
}
